import React from 'react';
import { LazyImg } from 'src/hooks/useLazyLoad';
import { usePhoneNumber } from 'src/util/getPhoneNumber';

import classes from './FreeReturns.scss';

const EMAIL = 'customersupport@workwearexpress.com';
const DPD_RETURN_URL = 'https://our-returns.dpd.co.uk/WORKWEAR_EXPRESS';

const handleSteps = [
    {
        id: 1,
        icon: require('../../../static/icons/message.svg'),
        content: (
            <p>
                Contact us by phone or
                <a href={`mailto: ${EMAIL}`}> email </a>
                with your order number
            </p>
        ),
    },
    {
        id: 2,
        icon: require('../../../static/icons/print.svg'),
        content: <p>We’ll send you a free return note, which you need to return your garments</p>,
    },
    {
        id: 3,
        icon: require('../../../static/icons/delivery.svg'),
        content: <p>Return the unworn garments to us in their original packaging</p>,
    },
    {
        id: 4,
        icon: require('../../../static/icons/currency.svg'),
        content: (
            <p>
                Leave the rest to us! We’ll provide a full refund to your original payment method within 5 working days
                of receiving the returned items
            </p>
        ),
    },
];

export const FreeReturns: React.FC = () => {
    const phoneNumber = usePhoneNumber('spaced');
    return (
        <div className={classes.freeReturns}>
            <div className={classes.infoBlock}>
                <div className={classes.info}>
                    <h1>Free Returns</h1>
                    <p className={classes.subtitle}>Not perfect? Return it and let us fix things</p>
                    <div className={classes.returnsContent}>
                        <div className={classes.returnsContentItem}>
                            <span className={classes.returnsContentItemTitle}>Plain (Non-Customised)</span>
                            <span className={classes.returnsContentItemDescription}>
                                Plain/non-customised items can be returned within <b>28 days</b> of receipt for a refund
                                or exchange, provided they are unused and in their original condition.
                            </span>
                        </div>
                        <div className={classes.returnsContentItem}>
                            <span className={classes.returnsContentItemTitle}>Customised Products</span>
                            <span className={classes.returnsContentItemDescription}>
                                If you’re unhappy with a customised or personalised item, please contact our Customer
                                Support team at <a href={`mailto: ${EMAIL}`}>{EMAIL}</a>
                            </span>
                        </div>
                        <span className={classes.returnsNote}>Please note this excludes international returns.</span>
                    </div>
                </div>
                <div className={classes.image}>
                    <LazyImg
                        src={require('../../../static/img/free-returns-img1.png')}
                        alt="Free returns on workwear orders"
                    />
                </div>
            </div>
            <div className={classes.returnCard}>
                <div className={classes.cardHeader}>
                    <h2>
                        <span className={classes.stepNumber}>1.</span> DPD Self Return
                    </h2>
                    <p>
                        If you are returning your order for any other reason, we ask that you organise the return of the
                        items. We use DPD’s free and convenient service for returns.
                    </p>
                </div>
                <div className={classes.dpdAction}>
                    <LazyImg src={require('../../../static/img/dpd.png')} alt="DPD" />
                    <a href={DPD_RETURN_URL} target="_blank" rel="noreferrer" className={classes.dpdButton}>
                        Create Return with DPD
                    </a>
                </div>
            </div>

            <div className={classes.returnCard}>
                <div className={classes.cardHeader}>
                    <h2>
                        <span className={classes.stepNumber}>2.</span> We'll handle the tricky ones
                    </h2>
                    <p>
                        If the garment is customised, damaged, or too large to return via DPD we can handle the return.
                    </p>
                </div>
                <div className={classes.divider} />
                <div className={classes.handleSteps}>
                    {handleSteps.map((step) => (
                        <div className={classes.handleStep} key={step.id}>
                            <span className={classes.handleStepNumber}>{step.id}</span>
                            <LazyImg src={step.icon} alt="" />
                            {step.content}
                        </div>
                    ))}
                </div>
                <div className={classes.divider} />
                <div className={classes.contact}>
                    <div className={classes.contactItem}>
                        <LazyImg src={require('../../../static/icons/orange-mail.svg')} alt="" />
                        <div>
                            <span className={classes.contactLabel}>Email us</span>
                            <a href={`mailto: ${EMAIL}`} className={classes.contactValue}>
                                {EMAIL}
                            </a>
                        </div>
                    </div>
                    <div className={classes.contactItem}>
                        <LazyImg src={require('../../../static/icons/orange-phone.svg')} alt="" />
                        <div>
                            <span className={classes.contactLabel}>Call us</span>
                            <span className={classes.contactValue}>{phoneNumber}</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className={classes.summary}>
                <div>
                    <h3>If we can’t accept a return</h3>
                    <p>
                        We will replace all faulty customised items. However, we cannot provide returns on customised
                        items that are not faulty. For that reason, we ask you to take your time when designing your
                        custom clothing before submitting them for production. For health and hygiene reasons, we cannot
                        exchange or refund opened underwear/lingerie, control wear, hosiery, and swimwear unless faulty.
                        We can exchange or refund these items if hygiene strips and tags are in place and the original
                        packaging is unopened and unmarked.
                    </p>
                </div>
                <div className={classes.divider} />
                <div>
                    <h3>Please note</h3>
                    <p>
                        <b>You have 28 days from receiving your order to complete a return.</b> Once this period has
                        passed, items cannot be returned unless faulty. Priority deliveries paid for when ordering are
                        customer preference. Unless goods returned are faulty, delivery surcharges cannot be refunded.
                        Products must be in their original condition, unworn, unwashed, and with all original tags and
                        packaging intact. Workwear Express reserves the right to refuse any return if the returned items
                        do not comply with these Terms and Conditions. Non-compliant items may be returned to the
                        customer at their expense or be subject to a re-packaging fee.
                    </p>
                    <p>Our free returns service is not available from:</p>
                    <ul>
                        <li>Northern Ireland</li>
                        <li>International locations, including the Republic of Ireland</li>
                        <li>Scottish Highlands and Islands region</li>
                    </ul>
                    <p>
                        Please don’t hesitate to get in touch with our{' '}
                        <a href={`mailto:${EMAIL}`} className={classes.orangeLink}>
                            Customer Support team
                        </a>{' '}
                        for guidance and options available to you.
                    </p>
                </div>
            </div>
        </div>
    );
};
